import { useState, useEffect } from "react";

import { Link, useLocation } from "wouter";
import { SignedIn, SignedOut, UserButton } from "@clerk/clerk-react";
import { Plus, CalendarDays, LogIn, Sun, Moon, Shield, List, MapPin, Building2, Menu, Zap, Info, Star, Navigation } from "lucide-react";
import { Button } from "../ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { useTheme } from "../../hooks/useTheme";
import { config } from "../../lib/config";
import { useModeratorRole } from "../../hooks/useModeratorRole";
import { useViewMode } from "../../contexts/ViewModeContext";
import { useAdminMode } from "../../contexts/AdminModeContext";
import { AdminDevToolbar } from "../admin/AdminDevToolbar";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { cn } from "../../lib/utils";
import { Logo } from "../ui/Logo";

const ACCOUNT_TYPE_STYLES: Record<string, { label: string; className: string }> = {
  dancer:    { label: "Dancer",    className: "bg-blue-500/10 text-blue-400" },
  organizer: { label: "Organizer", className: "bg-purple-500/10 text-purple-400" },
  studio:    { label: "Studio",    className: "bg-emerald-500/10 text-emerald-400" },
};

export function Header() {
  const hasAuth = !!config.clerkPublishableKey;
  const { theme, toggle } = useTheme();
  const [location, navigate] = useLocation();
  const { isAnyModerator } = useModeratorRole();
  const { user } = useCurrentUser();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { viewMode, setViewMode } = useViewMode();
  const { isAdminMode, toggleAdminMode, clearAdminMode } = useAdminMode();

  // Disable admin mode if moderator access is lost
  useEffect(() => {
    if (!isAnyModerator && isAdminMode) {
      clearAdminMode();
    }
  }, [isAnyModerator, isAdminMode]);

  const closeMenu = () => setMobileMenuOpen(false);

  const isHome = location === "/events";
  const isVenues = location.startsWith("/venues");

  const tabs = [
    {
      label: "Browse By Event",
      icon: List,
      active: isHome && viewMode === "list",
      onClick: () => { if (!isHome) navigate("/events"); setViewMode("list"); },
    },
    {
      label: "Browse By Venue",
      icon: Building2,
      active: isVenues,
      onClick: () => navigate("/venues"),
    },
    {
      label: "Browse By Distance",
      icon: Navigation,
      active: isHome && viewMode === "nearby",
      onClick: () => { if (!isHome) navigate("/events"); setViewMode("nearby"); },
    },
    {
      label: "Calendar View",
      icon: CalendarDays,
      active: isHome && viewMode === "calendar",
      onClick: () => { if (!isHome) navigate("/events"); setViewMode("calendar"); },
    },
    {
      label: "Map View",
      icon: MapPin,
      active: isHome && viewMode === "map",
      onClick: () => { if (!isHome) navigate("/events"); setViewMode("map"); },
    },
  ];

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
        {/* Top row: logo + controls */}
        <div className="px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => { navigate("/events"); setViewMode("list"); }}
              className="text-foreground hover:opacity-75 transition-opacity"
              aria-label="NE Dances - Home"
            >
              <Logo className="h-10 w-auto" />
            </button>

          </div>

          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" asChild className="hidden sm:flex">
              <Link href="/about">About</Link>
            </Button>
            <button
              onClick={toggle}
              className="hidden sm:flex p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
              aria-label="Toggle theme"
            >
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            {isAnyModerator && (
              <button
                onClick={toggleAdminMode}
                title={isAdminMode ? "Disable Admin Mode" : "Enable Admin Mode"}
                className={cn(
                  "hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors",
                  isAdminMode
                    ? "bg-red-600 text-white hover:bg-red-700"
                    : "bg-secondary text-muted-foreground hover:text-foreground"
                )}
              >
                <Zap className="w-3.5 h-3.5" />
                Admin Mode
              </button>
            )}
            {hasAuth && (
              <>
                <SignedIn>
                  <Button variant="ghost" size="sm" asChild className="hidden sm:flex">
                    <Link href="/dashboard">
                      <CalendarDays className="w-4 h-4" />
                      My Events
                    </Link>
                  </Button>
                  <Button variant="ghost" size="sm" asChild className="hidden sm:flex">
                    <Link href="/following">
                      <Star className="w-4 h-4" />
                      Following
                    </Link>
                  </Button>
                  <div className="hidden sm:flex items-center gap-2">
                    <UserButton>
                      {isAnyModerator && (
                        <UserButton.MenuItems>
                          <UserButton.Action
                            label="Admin Panel"
                            labelIcon={<Shield className="w-4 h-4" />}
                            onClick={() => navigate("/admin")}
                          />
                        </UserButton.MenuItems>
                      )}
                    </UserButton>
                    {user && ACCOUNT_TYPE_STYLES[user.accountType] && (
                      <span className={cn(
                        "text-xs font-medium px-2 py-0.5 rounded-full capitalize",
                        ACCOUNT_TYPE_STYLES[user.accountType]!.className
                      )}>
                        {ACCOUNT_TYPE_STYLES[user.accountType]!.label}
                      </span>
                    )}
                  </div>
                </SignedIn>
                <SignedOut>
                  <Button variant="ghost" size="sm" asChild className="hidden sm:flex">
                    <Link href="/login">
                      <LogIn className="w-4 h-4" />
                      Sign In
                    </Link>
                  </Button>
                </SignedOut>
              </>
            )}
            <Button asChild size="sm" variant="contrast" className="hidden sm:flex">
              <Link href="/submit">
                <Plus className="w-4 h-4" />
                Submit Event
              </Link>
            </Button>

            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="sm:hidden p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
              aria-label="Open menu"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tab row: desktop nav tabs */}
        <div className="hidden sm:flex justify-center border-t border-border/50">
          <div className="flex px-4">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.label}
                  onClick={tab.onClick}
                  className={cn(
                    "flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors",
                    tab.active
                      ? "border-primary text-primary"
                      : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
                  )}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Admin dev toolbar — only when admin mode is on */}
        {isAdminMode && <AdminDevToolbar />}
      </header>

      {/* Mobile Sheet Menu */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent
          side="right"
          className="w-72 flex flex-col gap-0 p-0"
          onInteractOutside={(e) => {
            const target = e.target as HTMLElement;
            if (target?.closest?.('[class*="cl-"]')) {
              e.preventDefault();
            }
          }}
        >
          <SheetHeader className="px-6 py-5 border-b border-border">
            <SheetTitle className="text-left text-foreground">
              <Logo className="h-8 w-auto" />
            </SheetTitle>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-1">
            <Button asChild size="sm" variant="contrast" className="w-full mb-3" onClick={closeMenu}>
              <Link href="/submit">
                <Plus className="w-4 h-4" />
                Submit Event
              </Link>
            </Button>

            {/* Nav tabs as mobile links */}
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.label}
                  onClick={() => { tab.onClick(); closeMenu(); }}
                  className={cn(
                    "w-full flex items-center gap-3 px-2 py-3 rounded-lg transition-colors text-sm font-medium text-left",
                    tab.active
                      ? "bg-primary/10 text-primary"
                      : "hover:bg-secondary text-foreground"
                  )}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}

            <Link href="/about" onClick={closeMenu}>
              <div className="flex items-center gap-3 px-2 py-3 rounded-lg hover:bg-secondary transition-colors text-sm font-medium">
                <Info className="w-4 h-4" />
                About
              </div>
            </Link>

            <div className="border-t border-border my-3" />

            {/* Theme toggle */}
            <button
              onClick={toggle}
              className="w-full flex items-center gap-3 px-2 py-3 rounded-lg hover:bg-secondary transition-colors text-sm font-medium text-left"
            >
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              {theme === "dark" ? "Light Mode" : "Dark Mode"}
            </button>

            {hasAuth && (
              <>
                <SignedIn>
                  <Link href="/dashboard" onClick={closeMenu}>
                    <div className="flex items-center gap-3 px-2 py-3 rounded-lg hover:bg-secondary transition-colors text-sm font-medium">
                      <CalendarDays className="w-4 h-4" />
                      My Events
                    </div>
                  </Link>
                  <Link href="/following" onClick={closeMenu}>
                    <div className="flex items-center gap-3 px-2 py-3 rounded-lg hover:bg-secondary transition-colors text-sm font-medium">
                      <Star className="w-4 h-4" />
                      Following
                    </div>
                  </Link>
                  {isAnyModerator && (
                    <>
                      <Link href="/admin" onClick={closeMenu}>
                        <div className="flex items-center gap-3 px-2 py-3 rounded-lg hover:bg-secondary transition-colors text-sm font-medium">
                          <Shield className="w-4 h-4" />
                          Admin Panel
                        </div>
                      </Link>
                      <button
                        onClick={() => { toggleAdminMode(); closeMenu(); }}
                        className={cn(
                          "w-full flex items-center gap-3 px-2 py-3 rounded-lg transition-colors text-sm font-semibold text-left",
                          isAdminMode
                            ? "bg-red-600 text-white hover:bg-red-700"
                            : "hover:bg-secondary text-foreground"
                        )}
                      >
                        <Zap className="w-4 h-4" />
                        {isAdminMode ? "Admin Mode ON" : "Admin Mode"}
                      </button>
                    </>
                  )}
                </SignedIn>
                <SignedOut>
                  <Link href="/login" onClick={closeMenu}>
                    <div className="flex items-center gap-3 px-2 py-3 rounded-lg hover:bg-secondary transition-colors text-sm font-medium">
                      <LogIn className="w-4 h-4" />
                      Sign In
                    </div>
                  </Link>
                </SignedOut>
              </>
            )}

          </div>

          {hasAuth && (
            <SignedIn>
              <div className="px-6 py-4 border-t border-border flex items-center gap-3">
                <UserButton>
                  {isAnyModerator && (
                    <UserButton.MenuItems>
                      <UserButton.Action
                        label="Admin Panel"
                        labelIcon={<Shield className="w-4 h-4" />}
                        onClick={() => { navigate("/admin"); closeMenu(); }}
                      />
                    </UserButton.MenuItems>
                  )}
                </UserButton>
                {user ? (
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {user.name || user.email}
                    </p>
                    {ACCOUNT_TYPE_STYLES[user.accountType] && (
                      <span className={cn(
                        "text-xs font-medium px-1.5 py-0.5 rounded-full capitalize",
                        ACCOUNT_TYPE_STYLES[user.accountType]!.className
                      )}>
                        {ACCOUNT_TYPE_STYLES[user.accountType]!.label}
                      </span>
                    )}
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground">Account</span>
                )}
              </div>
            </SignedIn>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}
